import React, { useEffect, useState, useCallback } from 'react';
import apiClient from '../../services/api'; // Adjust path if needed
import { Category, Goal, Task } from '../../types'; // Adjust path if needed
import TaskList from '../../components/goal_planner/TaskList';
import TaskFilterTabs from '../../components/goal_planner/TaskFilterTabs';
import TaskForm from '../../components/goal_planner/TaskForm';
import { Button } from '../../components/ui/button';
import { PlusCircle } from 'lucide-react';

// Define valid sortable fields
type SortField = 'task_date' | 'category__name' | 'completed' | 'one_thing';
type SortDirection = 'asc' | 'desc';

const TasksPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]); // Needed for TaskForm
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTaskFilter, setActiveTaskFilter] = useState<string>('all');
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);
  
  // --- Sorting State ---
  const [sortField, setSortField] = useState<SortField>('task_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  // -------------------

  const fetchData = useCallback(async (currentSortField: SortField, currentSortDirection: SortDirection) => {
    setIsLoading(true);
    setError(null);
    try {
      // Construct ordering parameter
      const orderingParam = `${currentSortDirection === 'desc' ? '-' : ''}${currentSortField}`;
      
      // Fetch Tasks with ordering, Categories, and Goals
      const [taskRes, catRes, goalRes] = await Promise.all([
        apiClient.get('/goals/tasks/', { params: { ordering: orderingParam } }), // Add ordering param
        apiClient.get('/goals/categories/'),
        apiClient.get('/goals/goals/') // Fetch goals for TaskForm dropdown
      ]);
      setTasks(taskRes.data);
      setCategories(catRes.data);
      setGoals(goalRes.data);
      console.log(`Fetched Tasks (Sorted by ${orderingParam}):`, taskRes.data);
      console.log("Fetched Categories for Tasks page:", catRes.data);
      console.log("Fetched Goals for Tasks page:", goalRes.data);

    } catch (err: any) {
      console.error("Failed to fetch tasks page data:", err);
      setError(err.message || 'Failed to load tasks data.');
    } finally {
      setIsLoading(false);
    }
  }, []); // Add empty dependency array as it has no external dependencies except apiClient

  useEffect(() => {
    fetchData(sortField, sortDirection);
  }, [fetchData, sortField, sortDirection]); // Refetch when sort changes

  // --- Handle Sorting Change ---
  const handleSortChange = (field: SortField) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    // Fetching is handled by the useEffect hook watching sortField and sortDirection
  };
  // --------------------------

  const refetchTasks = useCallback(async () => {
      // Refetch data using the current sort state
      await fetchData(sortField, sortDirection);
  }, [fetchData, sortField, sortDirection]); // Dependencies for refetchTasks

  const handleTaskFilterChange = (filter: string) => {
    setActiveTaskFilter(filter);
    // Note: Filtering is currently done client-side AFTER fetching all tasks.
    // If performance becomes an issue with many tasks, backend filtering should be added.
  };
  
  // Client-side filtering remains the same for now
  const filteredTasks = tasks.filter(task => {
    if (activeTaskFilter === 'completed') return task.completed;
    if (activeTaskFilter === 'one_thing') return task.one_thing;
    if (activeTaskFilter === 'today') {
      const today = new Date().toISOString().split('T')[0];
      return task.task_date === today && !task.completed;
    }
    if (activeTaskFilter === 'upcoming') {
        const today = new Date().toISOString().split('T')[0];
        return task.task_date > today && !task.completed;
    }
    // Default 'all' shows all *fetched* tasks (sorting handled by backend)
    // Or apply additional client-side filtering if needed, e.g., show only incomplete for 'all'
    // Let's adjust 'all' to show only incomplete tasks for consistency with original logic
     if (activeTaskFilter === 'all') {
       return !task.completed;
     }
     return true; // Should not happen with defined filters, but keeps all tasks if filter is unknown
  });

  const handleAddTask = () => {
    setTaskToEdit(null);
    setIsTaskModalOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setTaskToEdit(task);
    setIsTaskModalOpen(true);
  };

  const clearTaskToEdit = () => setTaskToEdit(null);

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6">
      {/* Loading State */} 
      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <span className="ml-4 text-muted-foreground">Loading tasks...</span>
        </div>
      )}
      
      {/* Error State */} 
      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-md border border-destructive/30">
          <p><strong>Error:</strong> {error}</p>
        </div>
      )}

      {!isLoading && !error && (
        <div className="space-y-4 md:space-y-6">
           <div className="flex justify-between items-center">
              <h1 className="text-xl md:text-2xl font-semibold">Daily Tasks</h1>
              <Button onClick={handleAddTask} size="sm" className="flex items-center gap-2">
                <PlusCircle className="h-4 w-4" />
                <span>Add New Task</span>
              </Button>
            </div>
            <TaskFilterTabs 
              currentFilter={activeTaskFilter} 
              onFilterChange={handleTaskFilterChange} 
            />
            <TaskList 
              tasks={filteredTasks} 
              categories={categories}
              refreshTasks={refetchTasks}
              onEditTask={handleEditTask} 
              // Pass sorting props down
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
            />
        </div>
      )}

       {/* Task Modal */}
      <TaskForm 
        isOpen={isTaskModalOpen}
        setIsOpen={setIsTaskModalOpen}
        task={taskToEdit}
        goals={goals} 
        categories={categories}
        refreshTasks={refetchTasks} // Use the local refetch
        clearTaskToEdit={clearTaskToEdit}
      />
    </div>
  );
};

export default TasksPage; 